import React from 'react';
import { Text } from '@lnr-v2/ui';
import styles from './MetricDetailItem.module.css';

export interface MetricDetailItemProps {
  image?: string | null;
  imageAlt?: string;
  title: string;
  subtitle?: string;
  metric: string | number;
  metricLabel?: string;
  action?: {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary';
  };
  onClick?: () => void;
  className?: string;
}

export const MetricDetailItem: React.FC<MetricDetailItemProps> = ({
  image,
  imageAlt,
  title,
  subtitle,
  metric,
  metricLabel,
  action,
  onClick,
  className,
}) => {
  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick();
    }
  };

  const containerClasses = [
    styles.container,
    onClick && styles.clickable,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={containerClasses}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {image && (
        <div className={styles.imageWrapper}>
          <img
            src={image}
            alt={imageAlt || title}
            className={styles.image}
            loading="lazy"
            width="120"
            height="80"
          />
        </div>
      )}

      <div className={styles.content}>
        <div className={styles.info}>
          <Text size="sm" weight="semibold" className={styles.title}>
            {title}
          </Text>
          {subtitle && (
            <Text size="xs" className={styles.subtitle}>
              {subtitle}
            </Text>
          )}
        </div>

        <div className={styles.metricWrapper}>
          <Text size="md" weight="bold" className={styles.metric}>
            {typeof metric === 'number' ? metric.toLocaleString() : metric}
          </Text>
          {metricLabel && (
            <Text size="xs" className={styles.metricLabel}>
              {metricLabel}
            </Text>
          )}
        </div>
      </div>

      {action && (
        <button
          type="button"
          className={`${styles.actionButton} ${styles[action.variant || 'primary']}`}
          onClick={(e) => {
            e.stopPropagation();
            action.onClick();
          }}
        >
          {action.label}
        </button>
      )}
    </div>
  );
};
